package com.iptvplayer.web.ui.screen

import androidx.compose.runtime.Composable
import com.iptvplayer.web.ui.Theme
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun LandingScreen(onSignIn: () -> Unit, onGetStarted: () -> Unit) {
    Div(attrs = {
        classes("hero-bg")
        style { property("min-height", "100vh"); property("position", "relative"); property("overflow", "hidden") }
    }) {
        // subtle animated grid backdrop
        Div(attrs = { classes("grid-overlay"); style { property("position", "absolute"); property("inset", "0"); property("pointer-events", "none") } }) {}

        Div(attrs = { style { property("position", "relative"); property("max-width", "1080px"); property("margin", "0 auto"); property("padding", "0 22px 70px") } }) {

            // top bar
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.SpaceBetween)
                    property("padding", "22px 0")
                }
            }) {
                Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); property("gap", "10px") } }) {
                    Span(attrs = { style { property("font-size", "24px") } }) { Text("📺") }
                    Span(attrs = { style { property("font-weight", "800"); property("font-size", "19px"); property("letter-spacing", ".5px") } }) { Text("IPTV PLAYER") }
                }
                Button(attrs = {
                    classes("cta-ghost"); onClick { onSignIn() }
                    style {
                        property("background", "transparent"); property("color", Theme.text)
                        property("border", "1px solid ${Theme.border}"); property("border-radius", "10px")
                        property("padding", "9px 18px"); property("font-size", "14px"); property("font-weight", "600"); property("cursor", "pointer")
                    }
                }) { Text("Sign in") }
            }

            // hero
            Div(attrs = { style { property("text-align", "center"); property("padding", "60px 0 30px") } }) {
                Div(attrs = {
                    style {
                        property("display", "inline-flex"); alignItems(AlignItems.Center); property("gap", "8px")
                        property("padding", "6px 14px"); property("border", "1px solid rgba(124,58,237,.5)"); property("border-radius", "999px")
                        property("background", "rgba(124,58,237,.12)"); property("font-size", "12.5px"); property("font-weight", "700")
                        property("letter-spacing", "1.5px"); property("color", "#c7b6ff"); property("margin-bottom", "26px")
                    }
                }) {
                    Span(attrs = { classes("badge-dot"); style { property("width", "8px"); property("height", "8px"); property("border-radius", "50%"); property("background", "#22d3ee"); property("display", "inline-block") } }) {}
                    Text("LIVE TV · MOVIES · 4K")
                }

                H1(attrs = {
                    style {
                        property("font-size", "clamp(40px, 7vw, 76px)"); property("line-height", "1.04"); property("font-weight", "900")
                        property("margin", "0 0 18px"); property("letter-spacing", "-1.5px")
                    }
                }) {
                    Span { Text("Stream ") }
                    Span(attrs = { classes("glow-title") }) { Text("everything.") }
                    Br {}
                    Span { Text("Anywhere.") }
                }

                P(attrs = {
                    style {
                        property("max-width", "620px"); property("margin", "0 auto 34px"); property("color", Theme.textDim)
                        property("font-size", "clamp(16px, 2.2vw, 19px)"); property("line-height", "1.6")
                    }
                }) {
                    Text("Your Xtream & M3U providers, thousands of live channels and movies, favorites, resume, and remote-cast — all in one blazing-fast player that follows you across web, mobile, and TV.")
                }

                Div(attrs = { style { display(DisplayStyle.Flex); justifyContent(JustifyContent.Center); property("gap", "14px"); property("flex-wrap", "wrap") } }) {
                    Button(attrs = {
                        classes("cta-primary"); onClick { onGetStarted() }
                        style {
                            property("color", "#fff"); property("border", "none"); property("border-radius", "12px")
                            property("padding", "15px 30px"); property("font-size", "16px"); property("font-weight", "800"); property("cursor", "pointer")
                        }
                    }) { Text("🚀  Get started — free") }
                    Button(attrs = {
                        classes("cta-ghost"); onClick { onSignIn() }
                        style {
                            property("background", "transparent"); property("color", Theme.text); property("border", "1px solid ${Theme.border}")
                            property("border-radius", "12px"); property("padding", "15px 28px"); property("font-size", "16px"); property("font-weight", "700"); property("cursor", "pointer")
                        }
                    }) { Text("I have an account") }
                }
            }

            // features
            Div(attrs = {
                style {
                    display(DisplayStyle.Grid); property("grid-template-columns", "repeat(auto-fit, minmax(260px, 1fr))")
                    property("gap", "16px"); property("margin-top", "56px")
                }
            }) {
                Feature("📡", "Live TV & Movies", "Xtream Codes & M3U playlists. Thousands of live channels plus on-demand movies and shows, organized by category.")
                Feature("▶️", "Instant playback", "HLS and MPEG-TS stream straight in your browser — no installs. Picks up right where you left off.")
                Feature("⭐", "Favorites & history", "Star what you love and your watch history follows you on every device you sign in to.")
                Feature("📲", "Remote play", "Push a stream from the web to your phone or TV in one tap — your devices, in sync.")
                Feature("☁️", "Sources that sync", "Your providers live on your account: survive reinstalls and appear everywhere automatically.")
                Feature("🔒", "Private & secure", "JWT auth, encrypted in transit. Your sources and data stay yours.")
            }

            // platforms
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex); justifyContent(JustifyContent.Center); property("gap", "26px"); property("flex-wrap", "wrap")
                    property("margin-top", "56px"); property("color", Theme.textDim); property("font-size", "15px")
                }
            }) {
                listOf("🌐 Web", "🤖 Android", "📺 Android TV", "🍎 iOS").forEach {
                    Span(attrs = { style { property("font-weight", "600") } }) { Text(it) }
                }
            }

            // bottom CTA
            Div(attrs = {
                style {
                    property("text-align", "center"); property("margin-top", "64px"); property("padding", "44px 24px")
                    property("border", "1px solid ${Theme.border}"); property("border-radius", "20px")
                    property("background", "linear-gradient(180deg, rgba(124,58,237,.10), rgba(79,140,255,.05))")
                }
            }) {
                Div(attrs = { style { property("font-size", "clamp(22px, 3.5vw, 30px)"); property("font-weight", "800"); property("margin-bottom", "10px") } }) { Text("Ready to watch?") }
                P(attrs = { style { property("color", Theme.textDim); property("margin", "0 0 22px") } }) { Text("Create your account and add a source in under a minute.") }
                Button(attrs = {
                    classes("cta-primary"); onClick { onGetStarted() }
                    style {
                        property("color", "#fff"); property("border", "none"); property("border-radius", "12px")
                        property("padding", "15px 34px"); property("font-size", "16px"); property("font-weight", "800"); property("cursor", "pointer")
                    }
                }) { Text("Get started") }
            }
        }
    }
}

@Composable
private fun Feature(icon: String, title: String, desc: String) {
    Div(attrs = {
        classes("feature-card")
        style {
            property("background", "rgba(255,255,255,.03)"); property("border", "1px solid ${Theme.border}")
            property("border-radius", "16px"); property("padding", "22px")
        }
    }) {
        Div(attrs = { style { property("font-size", "30px"); property("margin-bottom", "12px") } }) { Text(icon) }
        Div(attrs = { style { property("font-size", "17px"); property("font-weight", "700"); property("margin-bottom", "7px") } }) { Text(title) }
        P(attrs = { style { property("color", Theme.textDim); property("font-size", "14px"); property("line-height", "1.55"); property("margin", "0") } }) { Text(desc) }
    }
}
